import React, { memo, useRef, useEffect } from "react";
import { ComplexAvatarContainer } from "@xkit-yx/im-kit-ui";
import { formatMsgTime, msgRowHeights } from "./utils";
import TextMessage from "./TextMessage";
import ImageMessage from "./ImageMessage";
import VideoMessage from "./VideoMessage";
import AudioMessage from "./AudioMessage";
import FileMessage from "./FileMessage";
import TipMessage from "./TipMessage";
import NotificationMessage, { shouldShowNotification } from "./NotificationMessage";
import CustomMessage from "./CustomMessage";
import HonorIconGif from "../components/HonorIconGif";

// ── props 设计原则：只传原始类型/稳定引用，让 React.memo 生效 ─────────────
// 父组件（IMChatMobile）传入细粒度衍生值，避免整个集合作为 prop：
//   isCollapsing    ← 替代 collapsingIds Set
//   isActionTarget  ← 替代 actionTarget 对象
//   prevMsgTime     ← 替代 msgs 数组（仅用于时间分隔）
//   isPlusOne       ← 替代 plusOneTextMsg 对象（仅用于显示 +1 按钮）

const MessageRow = memo(function MessageRow({
  msg,
  prevMsgTime,
  isFlashing,
  isPlusOne,
  setActionTarget,
  onMessageAction,
  handlePlusOne,
  insertMention,
  nickMembers,
  findAccountFilter,
  replyMsgsMap,
  getEmojiConfig,
}) {
  const { type, flow, idClient } = msg;
  const isSelf    = flow === "out";
  const account   = msg.from;
  // 保存上一个 pressable 元素，使 ref 回调为 null（卸载）时仍能执行清理
  const pressableElRef = useRef(null);

  // ── 撤回提示：居中展示"xx 撤回了一条消息" ────────────────────────────
  if (type === "custom" && (msg.attach?.type === "reCallMsg" || msg.attach?.type === "beReCallMsg")) {
    const isRecallBySelf = msg.attach?.type === "reCallMsg" || isSelf;
    const recallNick = nickMembers?.find(e => e.account === msg.from)?.nickInTeam || msg.fromNick || msg.from;
    const recallTip = isRecallBySelf ? "你撤回了一条消息" : `${recallNick} 撤回了一条消息`;
    return (
      <div className="imc-msg-noti">
        <div className="imc-noti-center">{recallTip}</div>
      </div>
    );
  }

  // ── 系统/通知类型居中渲染 ────────────────────────────────────────────
  if (type === "tip") {
    return <div className="imc-msg-tip"><TipMessage msg={msg} /></div>;
  }
  if (type === "notification") {
    if (!shouldShowNotification(msg)) return null;
    return <div className="imc-msg-noti"><NotificationMessage msg={msg} /></div>;
  }

  // ── 普通/自定义消息内容 ──────────────────────────────────────────────
  const renderContent = () => {
    switch (type) {
      case "text":   return <TextMessage   msg={msg} replyMsgsMap={replyMsgsMap} getEmojiConfig={getEmojiConfig} />;
      case "image":  return <ImageMessage  msg={msg} />;
      case "video":  return <VideoMessage  msg={msg} />;
      case "audio":  return <AudioMessage  msg={msg} />;
      case "file":   return <FileMessage   msg={msg} />;
      case "custom": return <CustomMessage msg={msg} />;
      default:       return null;
    }
  };

  const content = renderContent();
  if (!content) return null;

  const canShowActions =
    type === "text" ||
    type === "custom" ||
    type === "image" ||
    type === "video";

  const showTimeSep = !prevMsgTime || msg.time - prevMsgTime > 5 * 60 * 1000;

  const nick = nickMembers.find((e) => e.account === msg.from)?.nickInTeam || msg.fromNick;
  const honorInfo = findAccountFilter(msg.from);
  const honorIcon = honorInfo?.honor_icon || null;

  // 长按菜单已移至 IMChatMobile 顶层渲染（position:fixed），
  // 避免被 imc-msg-list 的 -webkit-overflow-scrolling:touch stacking context 遮挡。
  // MessageRow 只负责触发长按并上报 { msg, rect }。
  const bubbleContent = canShowActions ? (
    <div
      className="imc-msg-content imc-msg-pressable"
      onContextMenu={(e) => {
        e.preventDefault();
        setActionTarget(msg, e.currentTarget.getBoundingClientRect());
      }}
      ref={(el) => {
        // 先清理上一个元素的监听器（包含 el=null 的卸载场景）
        const prev = pressableElRef.current;
        if (prev && prev._imcCleanup) { prev._imcCleanup(); prev._imcCleanup = null; }
        pressableElRef.current = el;
        if (!el) return;
        const onTouchStart = () => {
          el._longPressed = false;
          const t = setTimeout(() => {
            el._longPressed = true;
            // getBoundingClientRect 在 setTimeout 内调用，此时元素仍在视口中
            setActionTarget(msg, el.getBoundingClientRect());
          }, 500);
          const clear = () => clearTimeout(t);
          el.addEventListener("touchend",  clear, { once: true });
          el.addEventListener("touchmove", clear, { once: true });
        };
        // passive:true：不再需要 preventDefault，CSS 已通过 user-select/touch-callout 阻止系统菜单
        el.addEventListener("touchstart", onTouchStart, { passive: true });
        el._imcCleanup = () => el.removeEventListener("touchstart", onTouchStart);
      }}
      onClickCapture={(e) => {
        if (e.currentTarget._longPressed) { e.stopPropagation(); e.preventDefault(); }
      }}
    >
      {content}
    </div>
  ) : (
    <div className="imc-msg-content">{content}</div>
  );

  return (
    <React.Fragment>
      {showTimeSep && (
        <div className="imc-time-separator">{formatMsgTime(msg.time)}</div>
      )}
      <div
        id={idClient}
        className={`imc-msg-row${isSelf ? " imc-msg-row-self" : ""}${isFlashing ? " imc-msg-flash" : ""}`}
        style={msgRowHeights.has(idClient) ? { minHeight: msgRowHeights.get(idClient) } : undefined}
        ref={(el) => { if (el) msgRowHeights.set(idClient, el.offsetHeight); }}
      >
        <div
          className={`imc-avatar-wrap${isSelf ? "" : " imc-avatar-wrap-clickable"}`}
          onTouchStart={(e) => {
            if (isSelf) return;
            const el = e.currentTarget;
            el._lpTimer = setTimeout(() => {
              el._lpFired = true;
              const mentionNick =
                nickMembers.find((m) => m.account === msg.from)?.nickInTeam ||
                msg.fromNick ||
                msg.from;
              insertMention(mentionNick, msg.from);
            }, 500);
          }}
          onTouchMove={(e) => {
            // 手指移动超过 8px 视为滑动，取消长按
            const el = e.currentTarget;
            clearTimeout(el._lpTimer);
          }}
          onTouchEnd={(e) => {
            const el = e.currentTarget;
            clearTimeout(el._lpTimer);
            if (el._lpFired) {
              e.preventDefault(); // 长按触发后阻止后续 click
              el._lpFired = false;
            }
          }}
        >
          <ComplexAvatarContainer account={account} size={46} canClick={false} />
        </div>

        <div className={`imc-msg-main${isSelf ? " imc-msg-main-self" : ""}`}>
          <div className="imc-msg-name">
            <span>{nick}</span>
            {honorIcon && <HonorIconGif src={honorIcon} />}
          </div>
          <div className="imc-bubble-row">
            {isPlusOne && isSelf && (
              <img className="imc-plus-one-btn" src="resources/250424/+1.png" alt="+1" onClick={handlePlusOne} />
            )}
            {bubbleContent}
            {isPlusOne && !isSelf && (
              <img className="imc-plus-one-btn" src="resources/250424/+1.png" alt="+1" onClick={handlePlusOne} />
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
});

export default MessageRow;
